import { useEffect, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, m } from 'motion/react';
import type { LucideIcon } from 'lucide-react';
import { ArrowUpDown, ChevronDown, Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { useOverlays } from './overlay-context';

const SEARCH_DEBOUNCE_MS = 200;

export type ListFilterChipOption = {
  value: string;
  label: string;
};

export type ListFilterChip = {
  id: string;
  label: string;
  icon: LucideIcon;
  options: ListFilterChipOption[];
  selected: string[];
  onToggle: (value: string) => void;
};

export type ListQuickFilter = {
  id: string;
  label: string;
  active: boolean;
  onToggle: () => void;
};

export type ListSortOption = {
  value: string;
  label: string;
};

function FilterChip({
  label,
  count,
  icon: Icon,
  children,
}: {
  label: string;
  count: number;
  icon: LucideIcon;
  children: ReactNode;
}) {
  const { pushBlock, popBlock } = useOverlays();

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) pushBlock();
        else popBlock();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 gap-1 px-2 font-normal text-muted-foreground',
            count > 0 && 'bg-muted font-medium text-foreground',
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {label}
          {count > 0 ? (
            <span className="font-mono text-xs tabular-nums text-muted-foreground">{count}</span>
          ) : null}
          <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MoreFiltersMenu({ chips }: { chips: ListFilterChip[] }) {
  const { pushBlock, popBlock } = useOverlays();
  const count = chips.reduce((sum, chip) => sum + chip.selected.length, 0);

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) pushBlock();
        else popBlock();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn(
            'h-8 gap-1 px-2 font-normal text-muted-foreground',
            count > 0 && 'bg-muted font-medium text-foreground',
          )}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
          More filters
          {count > 0 ? (
            <span className="font-mono text-xs tabular-nums text-muted-foreground">{count}</span>
          ) : null}
          <ChevronDown className="h-3 w-3 opacity-70" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {chips.map((chip, index) => (
          <div key={chip.id}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{chip.label}</DropdownMenuLabel>
            {chip.options.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={chip.selected.includes(option.value)}
                onCheckedChange={() => chip.onToggle(option.value)}
              >
                {option.label}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ListFilterBar({
  query,
  onQueryChange,
  searchPlaceholder,
  searchAriaLabel,
  chips = [],
  moreChips = [],
  afterSearch,
  afterChips,
  sort,
  trailing,
  searchClassName,
  onClear,
  quick = [],
}: {
  query: string | null;
  onQueryChange: (query: string | null) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;
  chips?: ListFilterChip[];
  moreChips?: ListFilterChip[];
  /** One-tap filters that stay visible, e.g. "Mine". */
  quick?: ListQuickFilter[];
  afterSearch?: ReactNode;
  afterChips?: ReactNode;
  sort?: { value: string; options: ListSortOption[]; onChange: (value: string) => void };
  trailing?: ReactNode;
  searchClassName?: string;
  onClear: () => void;
}) {
  const [queryDraft, setQueryDraft] = useState(query ?? '');
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;

  useEffect(() => {
    setQueryDraft(query ?? '');
  }, [query]);

  useEffect(() => {
    const trimmed = queryDraft.trim() || null;
    if (trimmed === (query ?? null)) return;
    const handle = window.setTimeout(() => {
      onQueryChangeRef.current(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, queryDraft]);

  function handleClear() {
    setQueryDraft('');
    onClear();
  }

  const activeCount = [...chips, ...moreChips].reduce((sum, chip) => sum + chip.selected.length, 0);
  const hasPanel = chips.length > 0 || moreChips.length > 0 || Boolean(afterChips) || Boolean(sort);
  // Filters stay tucked away until someone asks for them, or one is already on.
  const [panelOpen, setPanelOpen] = useState(activeCount > 0);
  const showClear = activeCount > 0 || Boolean(query);

  const searchBox = (
    <div className={cn('relative w-full sm:max-w-xs sm:flex-1', searchClassName)}>
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        value={queryDraft}
        onChange={(event) => setQueryDraft(event.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchAriaLabel}
        className="h-9 rounded-lg bg-background pl-8 shadow-none"
      />
    </div>
  );

  const clearButton = showClear ? (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 shrink-0 font-normal text-muted-foreground"
      onClick={handleClear}
    >
      <X className="h-3.5 w-3.5" aria-hidden />
      Clear
    </Button>
  ) : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {searchBox}
        {afterSearch}
        {quick.map((button) => (
          <Button
            key={button.id}
            type="button"
            size="sm"
            variant="outline"
            aria-pressed={button.active}
            className={cn('h-8', button.active && 'border-primary bg-primary/10 text-primary hover:bg-primary/15')}
            onClick={button.onToggle}
          >
            {button.label}
          </Button>
        ))}
        {hasPanel ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-expanded={panelOpen}
            className={cn('h-8 gap-1.5', activeCount > 0 && 'border-primary text-primary')}
            onClick={() => setPanelOpen((open) => !open)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            Filter
            {activeCount > 0 ? (
              <span className="rounded-full bg-primary px-1.5 text-[11px] font-semibold tabular-nums text-primary-foreground">
                {activeCount}
              </span>
            ) : null}
          </Button>
        ) : null}
        {clearButton}
        {trailing ? <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-1.5">{trailing}</div> : null}
      </div>
      <AnimatePresence initial={false}>
        {hasPanel && panelOpen ? (
          <m.div
            key="filters"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-2 py-1.5" aria-label="Filters">
              {chips.map((chip) => (
                <FilterChip key={chip.id} label={chip.label} count={chip.selected.length} icon={chip.icon}>
                  {chip.options.map((option) => (
                    <DropdownMenuCheckboxItem
                      key={option.value}
                      checked={chip.selected.includes(option.value)}
                      onCheckedChange={() => chip.onToggle(option.value)}
                    >
                      {option.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </FilterChip>
              ))}
              {moreChips.length > 0 ? (
                <MoreFiltersMenu chips={moreChips} />
              ) : null}
              {afterChips}
              {sort ? (
                <div className="ml-auto">
                  <Select value={sort.value} onValueChange={sort.onChange}>
                    <SelectTrigger
                      aria-label="Sort"
                      className="h-8 w-auto min-w-[9rem] gap-1.5 border-none bg-transparent px-2 shadow-none"
                    >
                      <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                      <span className="text-muted-foreground">Sort:</span>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sort.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </div>
          </m.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
